/*!
  auto-specs-dawn.js
  - Finds "Specifications"/"Specifications:" content in the product description
  - Moves it into a new Dawn Collapsible Row placed **above**
    “FREE SHIPPING ON ALL ORDERS”
  - Matches native row: icon at left, chevron at right, divider line, spacing
  - Idempotent (won’t duplicate), supports Online Store 2.0 section reloads
*/

(function () {
  const TITLE = "SPECIFICATIONS";
  const ANCHOR_TEXT = "FREE SHIPPING ON ALL ORDERS"; // exact text of the row to insert above
  const TITLE_MATCH = /^\s*specifications\s*:?\s*$/i; // match “Specifications” and “Specifications:”

  // ----- small helpers ------------------------------------------------------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const norm = (str) => (str || "").replace(/\s+/g, " ").trim();

  function onReady(cb) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", cb, { once: true });
    } else cb();
  }

  // Supports OS2.0 live reload of product sections
  function observeSectionLoads(cb) {
    document.addEventListener("shopify:section:load", cb);
    document.addEventListener("shopify:section:unload", cb);
    document.addEventListener("shopify:section:select", cb);
    document.addEventListener("shopify:section:deselect", cb);
  }

  // ----- find description & extract the Specs block -------------------------
  function findDescriptionRoot(product) {
    // Common Dawn roots
    return (
      $(".product__description", product) ||
      $(".product__info-wrapper .rte", product) ||
      $(".rte", product) ||
      $(".product__info-container", product) ||
      null
    );
  }

  function isHeading(el) {
    return !!el && /^(H1|H2|H3|H4|H5|H6)$/i.test(el.tagName);
  }

  // Grab the heading “Specifications[:]?”, plus its following siblings
  // until the next heading of same/higher level or until we run out.
  function extractSpecsHTML(descRoot) {
    if (!descRoot) return null;

    // 1) look for a standalone heading with exact “Specifications/Specifications:”
    let heading =
      $$("h1,h2,h3,h4,h5,h6", descRoot).find((h) => TITLE_MATCH.test(norm(h.textContent))) ||
      null;

    // 2) fallback: look for a paragraph/strong that is just the word
    if (!heading) {
      heading =
        $$("p, strong, b", descRoot).find((n) => TITLE_MATCH.test(norm(n.textContent))) || null;
    }

    if (!heading) return null;

    // Determine boundary level for stopping (same/higher heading level)
    const startLevel = isHeading(heading) ? parseInt(heading.tagName.slice(1), 10) : 7;

    const collected = [];
    let node = heading;

    // If the heading itself contains useful HTML, include it ONLY when it has
    // relevant sibling inline content. We’ll omit the label in the accordion title.
    // Usually we want content AFTER the heading.
    node = heading.nextElementSibling;

    // Stop when we hit another heading of <= startLevel
    while (node) {
      if (isHeading(node)) {
        const level = parseInt(node.tagName.slice(1), 10);
        if (level <= startLevel) break;
      }
      // Safety: stop if we reached the collapsible rows container
      if (
        node.matches &&
        node.matches(".collapsible-content, .product__accordion, .accordion, .product__accordion-container")
      ) {
        break;
      }

      collected.push(node);
      node = node.nextElementSibling;
    }

    // If nothing collected, fallback to the immediate next sibling’s HTML
    if (!collected.length) return null;

    // Build HTML string & remove originals from description
    const frag = document.createElement("div");
    collected.forEach((n) => frag.appendChild(n.cloneNode(true)));
    // Remove originals
    collected.forEach((n) => n.parentElement && n.parentElement.removeChild(n));
    // Remove the “Specifications” label itself
    heading.parentElement && heading.parentElement.removeChild(heading);

    return frag.innerHTML.trim();
  }

  // ----- build an injected row by cloning the anchor row --------------------
  function makeDocIconLike(sampleIcon) {
    // Use similar visual weight as native icons (simple, thin strokes).
    // Paper with text lines.
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "icon icon--specs");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    // Match size & style of existing icons
    if (sampleIcon) {
      // copy rough sizing style so alignment matches
      svg.style.width = sampleIcon.style.width || "";
      svg.style.height = sampleIcon.style.height || "";
    }
    svg.innerHTML = `
      <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 3.75h7.5L18.75 9v11.25A2.25 2.25 0 0 1 16.5 22.5h-9A2.25 2.25 0 0 1 5.25 20.25V6a2.25 2.25 0 0 1 .75-2.25Z"/>
        <path d="M13.5 3.75V9h5.25"/>
        <path d="M8.25 12h7.5M8.25 15h7.5M8.25 18h4.5"/>
      </g>
    `;
    return svg;
  }

  function buildRowFromClone(anchor, innerHTML) {
    const clone = anchor.cloneNode(true);
    clone.removeAttribute("open");
    clone.setAttribute("data-injected-specs", "true");

    // Remove duplicate ids to keep DOM valid
    clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));

    // Ensure the thin divider line appears above the row like native items
    try {
      const cs = getComputedStyle(anchor);
      clone.style.borderTop = cs.borderTop;
      clone.style.marginTop = cs.marginTop;
      clone.style.paddingTop = cs.paddingTop;
      clone.style.paddingBottom = cs.paddingBottom;
    } catch (e) {}

    // Update icon (left) to our "document" icon
    const summary = $("summary", clone) || document.createElement("summary");
    const titleWrap = summary.querySelector(".summary__title") || summary;
    const leftIcon = titleWrap.querySelector("svg:not(.icon-caret)");
    const newIcon = makeDocIconLike(leftIcon);
    if (leftIcon) {
      leftIcon.replaceWith(newIcon);
    } else {
      titleWrap.insertBefore(newIcon, titleWrap.firstChild);
    }

    // Set the title text
    const titleEl =
      titleWrap.querySelector(".accordion__title") ||
      titleWrap.querySelector("h2, h3, .h2, .h3") ||
      titleWrap.querySelector("span") ||
      titleWrap;
    titleEl.textContent = TITLE;

    // Keep the chevron/caret as-is (Dawn uses .icon-caret in the summary)

    // Replace the content with the extracted Specs HTML
    const content =
      clone.querySelector(":scope > .accordion__content, :scope > div") ||
      document.createElement("div");
    content.innerHTML = innerHTML || "";
    if (!content.parentElement) clone.appendChild(content);

    return clone;
  }

  function findAnchorRow(product) {
    // Look for the row that has the exact anchor text in its summary title
    const rows = $$(".collapsible-content .accordion, .product__accordion", product)
      .flatMap((acc) => $$(":scope > details", acc));
    // Fallback: any details inside common containers
    const details = rows.length
      ? rows
      : $$(".collapsible-content details, .product__accordion details, .product__accordion-container details", product);

    return (
      details.find((d) => {
        const s = $("summary", d);
        if (!s) return false;
        const txt =
          norm(s.textContent) ||
          norm((s.querySelector(".accordion__title") || {}).textContent);
        return txt.toLowerCase() === ANCHOR_TEXT.toLowerCase();
      }) || null
    );
  }

  function alreadyInjected(product) {
    return $('[data-injected-specs="true"]', product);
  }

  // Main work: find specs, clone an anchor row, insert above anchor
  function injectSpecs(product) {
    if (alreadyInjected(product)) return;

    const descRoot = findDescriptionRoot(product);
    const html = extractSpecsHTML(descRoot);
    if (!html) return; // nothing to do

    const anchor = findAnchorRow(product);
    if (!anchor) {
      // No anchor found; don’t inject (keeps UI consistent)
      return;
    }

    const row = buildRowFromClone(anchor, html);
    anchor.parentElement.insertBefore(row, anchor);
  }

  // Run on load + on section updates
  function run() {
    const product = $(".product");
    if (!product) return;
    try {
      injectSpecs(product);
    } catch (e) {
      console.warn("[auto-specs] failed:", e);
    }
  }

  onReady(run);
  observeSectionLoads(run);

  // As a safety net, re-check when DOM changes inside product area
  const mo = new MutationObserver((muts) => {
    if (muts.some((m) => m.addedNodes.length || m.removedNodes.length)) run();
  });
  onReady(() => {
    const product = $(".product");
    if (product) mo.observe(product, { childList: true, subtree: true });
  });
})();